import { Effect, Layer } from 'effect';
import * as FetchHttpClient from '@effect/platform/FetchHttpClient';

import { ServiceConfig, Services } from '@fixtuur/backpack';
import { Manifest } from '@fixtuur/manifest-client';
import { Product } from '@fixtuur/gift-client';
import { Configuration, GavelConfig } from '@fixtuur/gavel-client';
import { useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';
import { useMemo } from 'preact/hooks';

export class ConfigLoadError {
    readonly _tag = 'ConfigLoadError';
    constructor() {}
}

export function useStellaLayer() {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);

    const stellaLayer = useMemo(() => constructStellaLayer(serviceConfig), [serviceConfig]);

    return stellaLayer;
}

export function constructStellaLayer(serviceConfig: ServiceConfig.ServiceConfig | undefined) {
    const apiLayers = Layer.provideMerge(
        Layer.mergeAll(
            Layer.provideMerge(
                Services.ModularTemplates.GavelModularTemplateService,
                Product.GiftProductService,
            ),
            Manifest.LiveReadManifestService,
            Configuration.DefaultConfigurationService,
        ),
        Layer.mergeAll(Services.Http.AxiosHttpService, FetchHttpClient.layer),
    );

    const giftConfigLayer = serviceConfig
        ? Layer.succeed(Product.GiftConfig, {
              giftUrl: Effect.succeed(serviceConfig.products),
          })
        : Layer.failSync(() => new ConfigLoadError());

    const gavelConfigLayer = serviceConfig
        ? Layer.succeed(Services.GavelConfig.GavelConfig, {
              gavelUrl: { base: serviceConfig.compatibility, path: '', query: {} },
          })
        : Layer.failSync(() => new ConfigLoadError());

    const gavelClientConfigLayer = serviceConfig
        ? Layer.succeed(GavelConfig, {
              baseUrl: Effect.succeed(serviceConfig.compatibility),
          })
        : Layer.failSync(() => new ConfigLoadError());

    const manifestConfigLayer = serviceConfig
        ? Layer.succeed(Manifest.ManifestConfigService, {
              manifestUrl: Effect.succeed(serviceConfig.manifest),
          })
        : Layer.failSync(() => new ConfigLoadError());

    const configLayers = Layer.mergeAll(
        giftConfigLayer,
        gavelConfigLayer,
        manifestConfigLayer,
        gavelClientConfigLayer,
    );

    return Layer.provide(apiLayers, configLayers);
}
